import { Fact } from '~/App/shared/interfaces/ProcessObject';
import { TranslateFunction } from '~/Locale';
import {
  translateFuel,
  translateGearbox,
  ProcessedAuctionStates
} from './auction';
import { getBaseCanonicalUrl } from './baseCanonicalUrl';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import { AuctionType } from '~/App/shared/interfaces/Auction';
import { getMonthlyLoanCost } from '~/App/views/ObjectPage/components/BiddingView/components/helpers';
import { formatMoney } from '~/helpers/currency';

interface PropertyValue {
  '@type': 'PropertyValue';
  name: string;
  value: string | number;
}

interface StructuredDataCar {
  '@context': string;
  '@type': string;
  name?: string;
  url?: string;
  description?: string;
  brand?: {
    '@type': string;
    name: string;
  };
  model?: string;
  vehicleModelDate?: string;
  vehicleIdentificationNumber?: string;
  dateVehicleFirstRegistered?: string;
  vehicleConfiguration?: string;
  mileageFromOdometer?: {
    '@type': string;
    value: string;
    unitCode: string;
  };
  fuelType?: string;
  vehicleTransmission?: string;
  numberOfForwardGears?: number;
  vehicleSeatingCapacity?: number;
  bodyType?: string;
  color?: string;
  vehicleInteriorType?: string;
  driveWheelConfiguration?: string;
  weightTotal?: {
    '@type': string;
    value: string;
    unitCode: 'KGM';
  };
  vehicleEngine?: {
    '@type': string;
    name?: string;
    engineType?: string;
    enginePower?: {
      '@type': string;
      value: string;
      unitCode: string;
    };
  };
  fuelConsumption?: {
    '@type': string;
    value: string;
    unitText: string;
  };
  emissionsCO2?: {
    '@type': string;
    value: string;
    unitText: string;
  };
  numberOfPreviousOwners?: number;
  image?: string[];
  additionalProperty?: PropertyValue[];
  offers?: {
    '@type': string;
    price?: string;
    lowPrice?: string;
    highPrice?: string;
    priceCurrency?: string;
    availability?: string;
    itemCondition?: string;
    priceValidUntil?: string;
    offerCount?: number;
    seller?: {
      '@type': string;
      name: string;
      url: string;
      telephone?: string;
      address?: {
        '@type': string;
        streetAddress?: string;
        addressLocality?: string;
        postalCode?: string;
        addressCountry?: string;
      };
    };
    url?: string;
    additionalProperty?: PropertyValue[];
  };
}

const findFactByKey = (
  facts: Fact[] | undefined,
  key: string
): Fact | undefined => {
  if (!facts) return undefined;
  return facts.find(fact => fact.key === key);
};

const findFactByType = (
  facts: Fact[] | undefined,
  type: string
): Fact | undefined => {
  if (!facts) return undefined;
  return facts.find(fact => fact.type === type);
};

const convertOdometerToKm = (value: number, unit: string): number => {
  switch (unit.toLowerCase()) {
    case 'mil':
      return Math.round(value * 10); // Swedish mil to km
    case 'miles':
      return Math.round(value * 1.60934); // English miles to km
    case 'km':
    case 'tim':
      return value;
    default:
      return value;
  }
};

export const generateVehicleStructuredData = ({
  t,
  auction,
  auctionStates,
  interestRate,
  canonicalUrl
}: {
  t: TranslateFunction;
  auction: ProcessedAuction;
  auctionStates: ProcessedAuctionStates;
  interestRate: number;
  canonicalUrl: string;
}): StructuredDataCar | null => {
  if (!auction || !auction.processObject) {
    return null;
  }

  const processObject = auction.processObject;
  const properties = processObject.properties;
  const allFacts = [
    ...(processObject.facts?.default || []),
    ...(processObject.facts?.additional || []),
    ...(processObject.facts?.other || [])
  ];
  const isAuction = auction.auctionType === AuctionType.BIDDING;
  const highestBid = auction?.activeAuction?.highestBid?.amount;
  const startBid = auction?.startBid;
  const buyNowPrice = auction?.buyNowAmount || 0;

  const baseUrl = getBaseCanonicalUrl();

  const structuredData: Partial<StructuredDataCar> = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: processObject?.title,
    url: canonicalUrl,
    description: auction.previewPreamble || undefined,
    brand: properties.brand
      ? {
          '@type': 'Brand',
          name: properties.brand
        }
      : undefined,
    model: properties.familyName,
    vehicleModelDate: properties.modelYear?.toString(),
    vehicleIdentificationNumber: properties.chassisNumber || undefined,
    dateVehicleFirstRegistered: properties.initialTrafficDate || undefined,
    vehicleConfiguration: processObject?.title || undefined
  };

  if (properties.odometerReading !== undefined) {
    const odometerKm = convertOdometerToKm(
      properties.odometerReading,
      properties.odometerUnit || 'km'
    );
    structuredData.mileageFromOdometer = {
      '@type': 'QuantitativeValue',
      value: odometerKm.toString(),
      unitCode: 'KMT' // Kilometers
    };
  }

  if (properties.fuels && properties.fuels.length > 0) {
    const fuelText = translateFuel(t, properties.fuels);
    if (fuelText) {
      structuredData.fuelType = fuelText;
    }
  }

  if (properties.gearbox) {
    const gearboxText = translateGearbox(t, properties.gearbox);
    if (gearboxText) {
      structuredData.vehicleTransmission = gearboxText;
    }
  }

  if (properties.numberOfSeats) {
    structuredData.vehicleSeatingCapacity = properties.numberOfSeats;
  }

  if (properties.body) {
    structuredData.bodyType = properties.body;
  }

  const exteriorColorFact = findFactByKey(allFacts, 'EXTERIOR_COLOR');
  if (typeof exteriorColorFact?.value === 'string') {
    structuredData.color = exteriorColorFact.value;
  }
  const textileFact = findFactByKey(allFacts, 'TEXTILE');
  if (typeof textileFact?.value === 'string') {
    structuredData.vehicleInteriorType = textileFact.value;
  }

  const enginePowerFact = findFactByType(allFacts, 'engine_power');
  const enginePowerKwFact = findFactByType(allFacts, 'engine_power_kw');
  const primaryFuel = properties.fuels?.[0];

  if (enginePowerFact || enginePowerKwFact || primaryFuel) {
    structuredData.vehicleEngine = {
      '@type': 'EngineSpecification'
    };

    let enginePowerHp;
    if (enginePowerFact && typeof enginePowerFact.value === 'number') {
      enginePowerHp = enginePowerFact.value;
    } else if (primaryFuel?.enginePowerHp) {
      enginePowerHp = primaryFuel.enginePowerHp;
    } else if (primaryFuel?.enginePower) {
      enginePowerHp = Math.round(primaryFuel.enginePower * 1.34102);
    } else if (
      enginePowerKwFact &&
      typeof enginePowerKwFact.value === 'number'
    ) {
      enginePowerHp = Math.round(enginePowerKwFact.value * 1.34102);
    }

    if (enginePowerHp) {
      structuredData.vehicleEngine.enginePower = {
        '@type': 'QuantitativeValue',
        value: enginePowerHp.toString(),
        unitCode: 'HPS' // Horsepower
      };
    }

    if (primaryFuel) {
      const fuelCode = primaryFuel.fuelCode?.toLowerCase();
      if (fuelCode?.includes('electric')) {
        structuredData.vehicleEngine.engineType = 'Electric';
      } else if (fuelCode?.includes('hybrid')) {
        structuredData.vehicleEngine.engineType = 'Hybrid';
      } else if (fuelCode === 'diesel') {
        structuredData.vehicleEngine.engineType = 'Diesel';
      } else if (
        fuelCode === 'petrol' ||
        fuelCode === 'petrol_ethanol' ||
        fuelCode === 'petrol_methane'
      ) {
        structuredData.vehicleEngine.engineType = 'Petrol';
      }
    }
  }

  // Total weight - The permitted total weight of the loaded vehicle, including passengers and cargo
  // and the weight of the empty vehicle
  const totalWeightFact = findFactByKey(allFacts, 'TOTAL_WEIGHT');
  if (totalWeightFact?.value) {
    structuredData.weightTotal = {
      '@type': 'QuantitativeValue',
      value: totalWeightFact.value.toString(),
      unitCode: 'KGM'
    };
  }

  const consumptionFact = findFactByType(allFacts, 'consumption');
  if (consumptionFact && typeof consumptionFact.value === 'number') {
    structuredData.fuelConsumption = {
      '@type': 'QuantitativeValue',
      value: consumptionFact.value.toString(),
      unitText:
        consumptionFact.key === 'CONSUMPTION_METHANE' ? 'kbm/100km' : 'L/100km'
    };
  } else if (primaryFuel?.consumption) {
    structuredData.fuelConsumption = {
      '@type': 'QuantitativeValue',
      value: primaryFuel.consumption.toString(),
      unitText:
        primaryFuel.fuelCode?.toLowerCase() === 'methane'
          ? 'kbm/100km'
          : 'L/100km'
    };
  }

  const emissionFact = findFactByType(allFacts, 'emission');
  if (emissionFact && typeof emissionFact.value === 'number') {
    structuredData.emissionsCO2 = {
      '@type': 'QuantitativeValue',
      value: emissionFact.value.toString(),
      unitText: 'g/km'
    };
  } else if (primaryFuel?.co2) {
    structuredData.emissionsCO2 = {
      '@type': 'QuantitativeValue',
      value: primaryFuel.co2.toString(),
      unitText: 'g/km'
    };
  }

  const previousOwnersFact = findFactByKey(
    allFacts,
    'NUMBER_OF_PREVIOUS_OWNERS'
  );
  if (previousOwnersFact && typeof previousOwnersFact.value === 'number') {
    structuredData.numberOfPreviousOwners = previousOwnersFact.value;
  }

  const images = processObject.media
    ?.filter(
      media =>
        media.contentType === 'image' &&
        media.type === 'PRESENTATION' &&
        media.uri
    )
    .map(media => media.uri);
  if (images && images.length > 0) {
    structuredData.image = images;
  }

  const additionalProperties: PropertyValue[] = [];

  if (processObject.package || auction.previewPackage) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Equipment Level',
      value: processObject.package || auction.previewPackage || ''
    });
  }

  const serviceBookTypeFact = findFactByKey(allFacts, 'SERVICE_BOOK_TYPE');
  if (serviceBookTypeFact?.value) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Servicebook',
      value: serviceBookTypeFact?.value
    });
  }

  const taxFeeFact = findFactByKey(allFacts, 'TAX_FEE');
  if (taxFeeFact?.value) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Tax per year',
      value: taxFeeFact?.value
    });
  }

  if (processObject.locationInfo?.facility?.city) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Location',
      value: processObject.locationInfo.facility.city
    });
  }

  const amount = isAuction ? highestBid || startBid : buyNowPrice;
  if (amount && amount > 0) {
    const monthlyCost = getMonthlyLoanCost({
      amount,
      interestRate
    });
    const monthlyCostString = formatMoney({
      value: monthlyCost,
      currency: t('sek/month')
    });
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Loan cost estimate',
      value: monthlyCostString
    });
  }

  if (additionalProperties.length > 0) {
    structuredData.additionalProperty = additionalProperties;
  }

  // Offers - Use AggregateOffer for auctions, regular Offer for buy-now
  const structuredDataOffersCommon: Partial<StructuredDataCar['offers']> = {
    priceCurrency: auction.currency || 'SEK',
    availability: auctionStates.isClosed
      ? 'https://schema.org/SoldOut'
      : 'https://schema.org/InStock',
    itemCondition: 'https://schema.org/UsedCondition',
    url: canonicalUrl,
    seller: {
      '@type': 'Organization',
      name: 'KVD',
      url: baseUrl
    }
  };

  if (
    processObject?.locationInfo?.facility &&
    structuredDataOffersCommon?.seller
  ) {
    const facility = processObject.locationInfo.facility;
    structuredDataOffersCommon.seller.address = {
      '@type': 'PostalAddress',
      streetAddress: facility?.address || undefined,
      addressLocality: facility?.city || undefined,
      postalCode: facility?.postCode || undefined,
      addressCountry: 'SE'
    };
    if (facility?.contactPhone) {
      structuredDataOffersCommon.seller.telephone = facility.contactPhone;
    }
  }

  if (isAuction) {
    const lowPrice = highestBid || startBid || 0;
    const highPrice = buyNowPrice > lowPrice ? buyNowPrice : lowPrice;
    const bidCount = auction?.activeAuction?.bids?.length || 0;
    const preliminaryCloseAt =
      auction?.activeAuction?.preliminaryCloseAt || auction?.closedAt;

    structuredData.offers = {
      '@type': 'AggregateOffer',
      lowPrice: lowPrice.toString(),
      highPrice: highPrice.toString(),
      offerCount: bidCount,
      ...structuredDataOffersCommon
    };

    const offersAdditionalProperties: PropertyValue[] = [];
    if (highestBid) {
      offersAdditionalProperties.push({
        '@type': 'PropertyValue',
        name: 'Current Bid',
        value: `${highestBid} ${auction.currency || 'SEK'}`
      });
    }
    if (preliminaryCloseAt) {
      offersAdditionalProperties.push({
        '@type': 'PropertyValue',
        name: 'Auction Ends',
        value: preliminaryCloseAt
      });
    }
    if (bidCount > 0) {
      offersAdditionalProperties.push({
        '@type': 'PropertyValue',
        name: 'Number of Bids',
        value: bidCount.toString()
      });
    }
    offersAdditionalProperties.push({
      '@type': 'PropertyValue',
      name: 'Auction Type',
      value: 'Online Auction'
    });

    structuredData.offers.additionalProperty = offersAdditionalProperties;
  } else {
    structuredData.offers = {
      '@type': 'Offer',
      price: buyNowPrice?.toString() || undefined,
      ...structuredDataOffersCommon
    };
  }

  return JSON.parse(JSON.stringify(structuredData)) as StructuredDataCar;
};
